"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/ui/Reveal";
import MediaSlideshow, { type Slide } from "@/components/ui/MediaSlideshow";
import { BOOK_DEMO_HREF } from "@/lib/marketing-links";

/**
 * The hero's single CTA, and the only action above the fold, so a little larger than the
 * other marketing CTAs. It goes straight to the demo form: the page is written for HR and
 * L&D buyers, and "Get started" in the navbar is the same destination.
 */
const ctaClassName =
  "group relative overflow-hidden flex items-center justify-center gap-size-sm bg-mkt-primary-container " +
  "text-white px-size-md sm:px-size-lg py-3 sm:py-4 rounded-full font-headline-md font-semibold " +
  "text-[clamp(18px,1.6vw,24px)] leading-tight text-center transition-all hover:bg-[#1a63ff] " +
  "hover:-translate-y-0.5 active:scale-95";

/**
 * Product screens from the enterprise workspace, in place of the old hero video
 * (/videos/hero-section-video.mp4, still in /public). Images and videos mix freely: an
 * image holds for a few seconds, a video plays through and then advances.
 */
const heroSlides: readonly Slide[] = [
  {
    type: "image",
    src: "/images/b2b-analysis.jpeg",
    alt: "inaraX Skill Intelligence: employee skill gaps with recommended training, skill gaps across the organization, proficiency by department and course completion",
  },
  {
    type: "video",
    src: "/videos/courses-personalization.mp4",
    label: "inaraX personalizing a course to each learner",
  },
  {
    type: "video",
    src: "/videos/assesment-course-page.mp4",
    label: "An inaraX skills assessment on a course page",
  },
  {
    type: "image",
    src: "/images/onboarding/remedial-button-preview.png",
    alt: "After a low assessment score, inaraX generates a personalized remedial lesson before the learner tries again",
    // Near-square screenshot: shown whole rather than cropped to 16:9, on its own edge
    // colour so it reads as filling the slideshow window rather than floating in it.
    fit: "contain",
    background: "#f8f9fb",
  },
];

export default function Hero() {
  return (
    // 100svh (not dvh/vh) minus the fixed navbar's 5rem: svh is the *smallest* viewport
    // height, so the section still fits once mobile browser chrome is showing, and it
    // doesn't resize mid-scroll the way dvh does — which would fight the ScrollTrigger
    // pin in HeroCoverTransition.
    <section className="relative isolate bg-mkt-navbar min-h-[calc(100svh_-_5rem)] flex items-center overflow-hidden px-margin-mobile md:px-margin-desktop">
      {/* Spacing here is fluid in both axes. The vh terms let a short laptop viewport
          reclaim the space a fixed py-size-xl/gap-size-md would spend — which is what used
          to push the CTAs below the fold — while the vw terms open the rhythm back up as
          the device gets wider. The stack gap sums both so neither dimension alone decides
          it: a wide-but-short window stays compact, a narrow-but-tall one stays tight. */}
      <div className="max-w-[1440px] mx-auto w-full flex flex-col items-center gap-[clamp(32px,4vw,64px)] py-[calc(clamp(24px,5vh,80px)_+_24px)] relative z-10">
        {/* Left-aligned on phones, where centred multi-line headings read raggedly; centred from md. */}
        <Reveal className="w-full md:w-auto min-w-0 flex flex-col items-start text-left md:items-center md:text-center gap-[clamp(12px,1vh_+_0.8vw,26px)]">
          {/* Arbitrary font-size, so the weight/tracking/leading the `display-lg` role would
              have bundled are restated here. The clamp tops out at that role's 64px. */}
          <h1 className="text-[clamp(30px,4.4vw,64px)] font-display-lg font-bold tracking-[-0.02em] text-white leading-[1.1]">
            AI learning built around your workforce.
          </h1>
          {/* Tagline between the h1 and the subtitle: sized about halfway between the two
              and semibold, so it reads as a second headline rather than body copy. */}
          <p className="-mt-1 text-[clamp(20px,2.4vw,36px)] font-headline-md font-semibold tracking-[-0.01em] leading-[1.2] text-white">
            Personalized by AI. Validated by human experts.
          </p>
          {/* Arbitrary size like the h1, so the body-lg role's 1.6 line-height is restated. */}
          {/*
	  <p className="text-[clamp(16px,1.5vw,21px)] font-body-lg leading-[1.6] text-white max-w-[60ch] text-balance">
            We build AI-ready teams that close skill gaps and turn AI fluency into real-world impact.
          </p>
	 */}
          {/* Extra top margin on top of the stack gap, fluid like the rest of the hero's spacing. */}
          <div className="mt-[clamp(8px,1.5vh_+_1vw,32px)]">
            <Link href={BOOK_DEMO_HREF} className={ctaClassName}>
              Book a Consultation
              <ArrowRight
                aria-hidden="true"
                strokeWidth={2}
                className="h-[1em] w-[1em] transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
          </div>
        </Reveal>
        {/* Stacked under the heading and centred, capped so it doesn't run the full 1440px.
            min-w-0 keeps the slideshow's width from pushing it off a phone screen. */}
        <Reveal delayMs={200} className="relative min-w-0 w-full max-w-7xl">
          {/* Grey-to-navy gradient frame, limited to the slideshow and lighter than the
              hero's navbar colour (mkt-navbar) so it reads as a frame. The slideshow's own surface is transparent, so the same gradient shows behind a slide that does
              not fill the 16:9 frame (fit: "contain") and reads as one continuous panel.
              No shadow, by design. */}
          <div className="relative z-20 rounded-3xl bg-gradient-to-br from-[#9aa3b2] via-[#4a5878] to-[#14244d] p-5 sm:p-10 md:p-14">
            <div className="overflow-hidden rounded-xl">
              <MediaSlideshow
                slides={heroSlides}
                label="inaraX product tour"
                sizes="(min-width: 1280px) 1280px, 100vw"
                priority
                surfaceClassName="bg-transparent"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
